import { ResponsiveBar } from '@nivo/bar';
import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import {
  useChannelListCommentsQuery,
  useChannelListPostsQuery,
  useChannelListReactionsQuery,
  useChannelListSharesQuery,
  useChannelListViewsQuery,
} from '../../../../../services';
import { ORG_DASH_ANALYTICS, Select, cookieOptions } from '../../../../../shared';
import { COLORS } from '../../Analytics';
import { ChartWrapper, Loader, getHorizontalBarsLayer } from '../../helpers';
import { ContentProps } from '../Content';

import classes from '../../Analytics.module.scss';

const BAR_HEIGHT = 32;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 40;
const PADDING = BAR_HEIGHT / 2;
const TYPE_LOCALISE_KEY = 'chartTypes.channel-list';

enum ChannelListType {
  POSTS = 'POSTS',
  VIEWS = 'VIEWS',
  REACTIONS = 'REACTIONS',
  COMMENTS = 'COMMENTS',
  SHARES = 'SHARES',
}

export const ChannelList: FunctionComponent<ContentProps> = memo(
  ({ from, to, channelId, platform }) => {
    const { t } = useTranslation();

    const [cookies, setCookie] = useCookies([ORG_DASH_ANALYTICS]);

    const { channelListType = ChannelListType.POSTS } = { ...cookies[ORG_DASH_ANALYTICS] };

    const dropdownItems = useMemo(() => {
      return Object.values(ChannelListType).map((value) => {
        return { id: value, title: t(`channelListTypes.${value}`) };
      });
    }, [t]);

    const channelListTypeChange = useCallback(
      (channelListType: ChannelListType) => {
        setCookie(
          ORG_DASH_ANALYTICS,
          { ...cookies[ORG_DASH_ANALYTICS], channelListType },
          cookieOptions()
        );
      },
      [cookies, setCookie]
    );

    const channelListQuery = useMemo(() => {
      switch (channelListType) {
        case ChannelListType.VIEWS:
          return useChannelListViewsQuery;
        case ChannelListType.REACTIONS:
          return useChannelListReactionsQuery;
        case ChannelListType.COMMENTS:
          return useChannelListCommentsQuery;
        case ChannelListType.SHARES:
          return useChannelListSharesQuery;
        default:
          return useChannelListPostsQuery;
      }
    }, [channelListType]);

    const { data: channelList, isFetching: isFetchingChannelList } = channelListQuery({
      from,
      to,
      channelId,
      platform,
    });

    const renderChart = useMemo(() => {
      if (isFetchingChannelList) {
        return <Loader />;
      }

      if (!channelList) {
        return null;
      }

      const { items } = channelList;

      const itemsFiltered = items.filter(({ value }) => value);

      if (!itemsFiltered.length) {
        return (
          <div className={classes['analytics__chart-no-records']}>{t('common.no-records')}</div>
        );
      }

      const keys: string[] = [];

      const data = itemsFiltered.map(({ label, value }) => {
        keys.push(label);
        return { channel: label, [label]: value };
      });

      const height = `calc(${data.length * BAR_HEIGHT}px + ${MARGIN_TOP}px + ${MARGIN_BOTTOM}px + ${
        (data.length + 1) * PADDING
      }px)`;

      return (
        <div style={{ height }}>
          <ResponsiveBar
            ariaLabel={t(TYPE_LOCALISE_KEY)}
            layout={'horizontal'}
            enableGridY={false}
            margin={{ top: MARGIN_TOP, right: 50, bottom: MARGIN_BOTTOM, left: 20 }}
            padding={0.5}
            borderRadius={4}
            tooltip={() => <></>}
            axisLeft={{
              renderTick: ({ value, y }) => (
                <text y={y - PADDING} fontSize={14} fill={'#1E1E1E'}>
                  {value}
                </text>
              ),
            }}
            axisBottom={{ format: (value) => (value === Math.floor(value) ? value : '') }}
            data={data.reverse()}
            keys={keys}
            indexBy={'channel'}
            colors={[COLORS[0]]}
            label={''}
            layers={['axes', 'bars', ({ bars }) => getHorizontalBarsLayer({ bars })]}
          />
        </div>
      );
    }, [channelList, isFetchingChannelList, t]);

    return (
      <ChartWrapper
        typeLocaliseKey={TYPE_LOCALISE_KEY}
        infoLocaliseKey={'channelList.info'}
        component={
          <Select
            className={classes['analytics__chart-header-select']}
            selectedItemId={channelListType}
            items={dropdownItems}
            onChange={(type) => channelListTypeChange(type as ChannelListType)}
          />
        }
      >
        {renderChart}
      </ChartWrapper>
    );
  }
);
